'use client';

import { c as _c } from "react/compiler-runtime";
import { Checkbox, Divider, Stack, Typography } from '@mui/material';
import type { ClassificationCategory, Condition, DataClassificationEntityOption } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { Loader } from '@/components/layout/loading';
import { HelpIcon } from '@/components/shared/helpIcon';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { PiiStrings } from '@/data/inventory/data';
import { useClassificationCategories } from '@/data/inventory/useClassificationCategories';
import { createValueCondition, isValueCondition } from '@/types/advanceFilter';
import { MultiSelectFilter, type FilterProps } from './filters';
const DATA_CLASSES = 'dataClasses';
const DATA_CLASS_ENTITIES = 'dataClassEntities';
const OTHER_CATEGORY = 'Other';
const DEFAULT_CLASS_NAMES = ['PII', 'FI', 'PHI'];
const isDefaultClass = (name: string) => DEFAULT_CLASS_NAMES.includes(name);
const isCustomClass = (name: string) => !isDefaultClass(name) && name !== OTHER_CATEGORY;
interface DataClassGroup {
  id: string;
  description?: string;
  children: string[];
}
const buildGroups = (categories: ClassificationCategory[], entities: DataClassificationEntityOption[]): DataClassGroup[] => {
  const groups = new Map<string, DataClassGroup>(categories.map(c => [c.name, {
    id: c.name,
    description: c.description,
    children: []
  }]));
  for (const entity of entities) {
    const cats = entity.categories.length ? entity.categories : [OTHER_CATEGORY];
    for (const cat of cats) {
      const group = groups.get(cat) ?? {
        id: cat,
        children: []
      };
      group.children.push(entity.name);
      groups.set(cat, group);
    }
  }
  return [...groups.values()].map(group => ({
    ...group,
    children: [...new Set(group.children)].sort()
  }));
};
const findValues = (conditions: Condition[], property: string): string[] => {
  const found = conditions.find(c => isValueCondition(c) && c.property === property);
  return found?.value ?? [];
};
const isDataClassCondition = (condition: Condition) => isValueCondition(condition) && (condition.property === DATA_CLASSES || condition.property === DATA_CLASS_ENTITIES);
const buildDataClassConditions = (classes: string[], entities: string[]): Condition[] => {
  const out: Condition[] = [];
  if (classes.length) {
    out.push(createValueCondition(DATA_CLASSES, classes));
  }
  if (entities.length) {
    out.push(createValueCondition(DATA_CLASS_ENTITIES, entities));
  }
  return out;
};
const stripDataClassConditions = (conditions: Condition[]): Condition[] => conditions.filter(c => !isDataClassCondition(c));
const ROW_GAP = '12px';
const CHECKBOX_PADDING = '3px';
const CHECKBOX_SLOT_WIDTH = '26px';
// marginLeft for child rows so the child checkbox sits under the parent arrow.
const CHILD_INDENT = `calc(${CHECKBOX_SLOT_WIDTH} + ${ROW_GAP})`;
export const entityLabel = (entity: string) => PiiStrings[entity] ?? entity;
const CUSTOM_CLASS_SUBTITLE = 'Manually added by Eon for your organization';
const CustomClassTooltip = t0 => {
  const $ = _c(5);
  const {
    description
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Divider sx={{
      borderColor: "currentColor",
      opacity: 0.2
    }} />;
    t2 = {
      gap: "8px"
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Stack direction="row" sx={{
      gap: "8px",
      alignItems: "center"
    }}><i className="material-symbols-settings-outline-rounded text-[24px]" />{CUSTOM_CLASS_SUBTITLE}</Stack>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== description) {
    t4 = <Stack divider={t1} sx={t2}>{description}{t3}</Stack>;
    $[3] = description;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const DataClassFilterGroup = t0 => {
  const $ = _c(47);
  const {
    group,
    isCustom,
    parentSelected,
    selectedEntities,
    onParentChange,
    onChildChange
  } = t0;
  let t1;
  if ($[0] !== group.children || $[1] !== selectedEntities) {
    let t2;
    if ($[3] !== selectedEntities) {
      t2 = c => selectedEntities.includes(c);
      $[3] = selectedEntities;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = group.children.some(t2);
    $[0] = group.children;
    $[1] = selectedEntities;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const childSelected = t1;
  const hasChildren = group.children.length > 0;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      alignItems: "center",
      gap: ROW_GAP,
      width: "100%"
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      width: CHECKBOX_SLOT_WIDTH,
      alignItems: "center",
      justifyContent: "center"
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = `filter-dataClasses-${group.id}`;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      padding: CHECKBOX_PADDING
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = !parentSelected && childSelected;
  let t7;
  if ($[8] !== onParentChange || $[9] !== parentSelected || $[10] !== t4 || $[11] !== t6) {
    t7 = <Stack sx={t3}><Checkbox data-testid={t4} sx={t5} size="small" checked={parentSelected} indeterminate={t6} onChange={onParentChange} /></Stack>;
    $[8] = onParentChange;
    $[9] = parentSelected;
    $[10] = t4;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = `material-symbols-arrow-right-rounded${hasChildren ? "" : " invisible"}`;
  let t9;
  if ($[13] !== t8) {
    t9 = <i className={t8} />;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      alignItems: "center",
      width: "100%"
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== onParentChange) {
    t11 = (text, ref) => <Typography ref={ref} variant="subtitle1" className="cursor-pointer truncate" sx={{
      minWidth: 0
    }} onClick={e => {
      e.stopPropagation();
      onParentChange();
    }}>{text}</Typography>;
    $[16] = onParentChange;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== group.id || $[19] !== t11) {
    t12 = <OverflowTooltip text={group.id} renderContent={t11} />;
    $[18] = group.id;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      marginLeft: "auto",
      justifyContent: "center"
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== group.description || $[23] !== group.id || $[24] !== isCustom) {
    t14 = isCustom ? <CustomClassTooltip description={group.description} /> : group.description ?? group.id;
    $[22] = group.description;
    $[23] = group.id;
    $[24] = isCustom;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      slotProps: {
        tooltip: {
          sx: {
            padding: "16px"
          }
        }
      }
    };
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14) {
    t16 = <Stack onClick={_temp2} sx={t13}><HelpIcon title={t14} className="cursor-default" tooltipProps={t15} /></Stack>;
    $[27] = t14;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t12 || $[30] !== t16) {
    t17 = <Stack direction="row" sx={t10}>{t12}{t16}</Stack>;
    $[29] = t12;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t17 || $[33] !== t9) {
    t18 = <AccordionSummary expandIcon={t9} sx={_temp}>{t17}</AccordionSummary>;
    $[32] = t17;
    $[33] = t9;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t18 || $[36] !== t7) {
    t19 = <Stack direction="row" className="group/help" sx={t2}>{t7}{t18}</Stack>;
    $[35] = t18;
    $[36] = t7;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== group.children || $[39] !== hasChildren || $[40] !== onChildChange || $[41] !== parentSelected || $[42] !== selectedEntities) {
    t20 = hasChildren && <AccordionDetails sx={{
      px: "0px",
      py: "4px"
    }}><Stack sx={{
        gap: "0px"
      }}>{group.children.map(entity => <Stack key={entity} direction="row" sx={{
          alignItems: "center",
          gap: ROW_GAP,
          marginLeft: CHILD_INDENT
        }}><Stack sx={{
            width: CHECKBOX_SLOT_WIDTH,
            alignItems: "center",
            justifyContent: "center"
          }}><Checkbox data-testid={`filter-dataClassEntities-${entity}`} sx={{
              padding: CHECKBOX_PADDING
            }} size="small" checked={parentSelected || selectedEntities.includes(entity)} onChange={() => onChildChange(entity)} /></Stack><OverflowTooltip text={entityLabel(entity)} renderContent={(text_0, ref_0) => <Typography ref={ref_0} variant="subtitle2" className="cursor-pointer truncate" sx={{
            minWidth: 0
          }} onClick={() => onChildChange(entity)}>{text_0}</Typography>} /></Stack>)}</Stack></AccordionDetails>;
    $[38] = group.children;
    $[39] = hasChildren;
    $[40] = onChildChange;
    $[41] = parentSelected;
    $[42] = selectedEntities;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t19 || $[45] !== t20) {
    t21 = <Accordion>{t19}{t20}</Accordion>;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
};
const DataClassesWithEntitiesFilter = ({
  conditions,
  onChange
}: FilterProps) => {
  const dal = useDAL();
  const {
    categories,
    isLoading: categoriesLoading
  } = useClassificationCategories();
  const {
    data: body,
    isLoading: entitiesLoading
  } = dal.inventory.entities.useList();
  const groups = useMemo(() => buildGroups(categories, body?.entities ?? []), [categories, body?.entities]);
  if (categoriesLoading || entitiesLoading) {
    return <Loader />;
  }
  const selectedClasses = findValues(conditions, DATA_CLASSES);
  const selectedEntities = findValues(conditions, DATA_CLASS_ENTITIES);
  const apply = (classes: string[], entities: string[]) => {
    onChange([...stripDataClassConditions(conditions), ...buildDataClassConditions(classes, entities)]);
  };
  const onParentChange = (group: DataClassGroup) => {
    const isOn = selectedClasses.includes(group.id);
    const nextClasses = isOn ? selectedClasses.filter(c => c !== group.id) : [...selectedClasses, group.id];
    const nextEntities = selectedEntities.filter(e => !group.children.includes(e));
    apply(nextClasses, nextEntities);
  };
  const onChildChange = (group_0: DataClassGroup, entity: string) => {
    const nextClasses_0 = selectedClasses.filter(c_0 => c_0 !== group_0.id);
    const otherEntities = selectedEntities.filter(e_0 => !group_0.children.includes(e_0));
    // Selecting a child while the parent class is selected swaps the broad
    // class for that single entity in the same group.
    if (selectedClasses.includes(group_0.id)) {
      apply(nextClasses_0, [...otherEntities, entity]);
      return;
    }
    const nextEntities_0 = selectedEntities.includes(entity) ? selectedEntities.filter(e_1 => e_1 !== entity) : [...selectedEntities, entity];
    apply(nextClasses_0, nextEntities_0);
  };
  const hasCustoms = categories.some(c_1 => isCustomClass(c_1.name));
  const defaultGroups = groups.filter(g => isDefaultClass(g.id));
  const customGroups = groups.filter(g_0 => isCustomClass(g_0.id));
  const otherGroups = groups.filter(g_1 => !isDefaultClass(g_1.id) && !isCustomClass(g_1.id));
  return <Stack sx={{
    gap: '4px'
  }}>
      {hasCustoms && <Typography variant='overline' sx={{
      paddingY: '8px'
    }}>
          Default classes
        </Typography>}
      {defaultGroups.map(group_1 => <DataClassFilterGroup key={group_1.id} group={group_1} isCustom={false} parentSelected={selectedClasses.includes(group_1.id)} selectedEntities={selectedEntities} onParentChange={() => onParentChange(group_1)} onChildChange={entity_0 => onChildChange(group_1, entity_0)} />)}
      {hasCustoms && <Typography variant='overline' sx={{
      paddingY: '8px'
    }}>
          Custom classes
        </Typography>}
      {customGroups.map(group_2 => <DataClassFilterGroup key={group_2.id} group={group_2} isCustom={true} parentSelected={selectedClasses.includes(group_2.id)} selectedEntities={selectedEntities} onParentChange={() => onParentChange(group_2)} onChildChange={entity_1 => onChildChange(group_2, entity_1)} />)}
      {otherGroups.map(group_3 => <DataClassFilterGroup key={group_3.id} group={group_3} isCustom={false} parentSelected={selectedClasses.includes(group_3.id)} selectedEntities={selectedEntities} onParentChange={() => onParentChange(group_3)} onChildChange={entity_2 => onChildChange(group_3, entity_2)} />)}
    </Stack>;
};
const FlatDataClassesFilter = props => {
  const $ = _c(8);
  const {
    categories,
    isLoading
  } = useClassificationCategories();
  let t0;
  if ($[0] !== categories) {
    bb0: {
      const toOption = _temp3;
      const defaults = categories.filter(_temp4).map(toOption);
      const customs = categories.filter(_temp5).map(toOption);
      if (customs.length === 0) {
        t0 = defaults;
        break bb0;
      }
      let t1;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          id: "__default_header",
          name: "Default classes",
          isGroupTitle: true
        };
        $[2] = t1;
      } else {
        t1 = $[2];
      }
      let t2;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          id: "__custom_header",
          name: "Custom classes",
          isGroupTitle: true
        };
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t0 = [t1, ...defaults, t2, ...customs];
    }
    $[0] = categories;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const options = t0;
  if (isLoading) {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Loader />;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== options || $[6] !== props) {
    t1 = <MultiSelectFilter {...props} options={options} />;
    $[5] = options;
    $[6] = props;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
export const DataClassesFilter = props => {
  const $ = _c(3);
  const {
    EnableInventoryEntitiesFilter
  } = useFeatureFlags();
  let t0;
  if ($[0] !== EnableInventoryEntitiesFilter || $[1] !== props) {
    t0 = EnableInventoryEntitiesFilter === true ? <DataClassesWithEntitiesFilter {...props} /> : <FlatDataClassesFilter {...props} />;
    $[0] = EnableInventoryEntitiesFilter;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
function _temp(theme) {
  return {
    flex: 1,
    flexDirection: "row-reverse",
    gap: ROW_GAP,
    padding: 0,
    "& .MuiAccordionSummary-content": {
      margin: 0,
      padding: 0
    },
    "& .MuiTypography-root": {
      fontWeight: theme.typography.fontWeightRegular
    }
  };
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(c) {
  return {
    id: c.name,
    name: c.name,
    description: isCustomClass(c.name) ? <CustomClassTooltip description={c.description} /> : c.description
  };
}
function _temp4(c_0) {
  return isDefaultClass(c_0.name);
}
function _temp5(c_1) {
  return isCustomClass(c_1.name);
}